"use client";

import { adminV2Receipts } from "@api/all";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  toast,
  Tooltip,
} from "@bleu.builders/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/Tabs";
import { withPermission } from "@components/WithAccessControl";
import { useProgram } from "@contexts/ProgramContext";
import { client } from "@utils/api-client";
import { Save } from "lucide-react";
import { serialize } from "object-to-formdata";
import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";

import {
  ReceiptUpdateProvider,
  useReceiptUpdate,
} from "./(components)/context";
import { ParticipantHistory } from "./(components)/participant-history";
import { ReceiptActivity } from "./(components)/receipt-activity";
import { ReceiptAnalysis } from "./(components)/receipt-analysis";
import { ReceiptDetailsCard } from "./(components)/receipt-details-card";
import { ReceiptItems } from "./(components)/receipt-items";
import { ReceiptSubmitIssue } from "./(components)/receipt-submit-issue";

export const fetcher = async (
  url,
  { arg }: { arg: { formData: FormData; method: string } },
) => {
  const { formData, method } = arg;

  const data = await client(url, { body: formData, method });
  return data;
};

export function ReceiptOverview() {
  const { mutate } = useSWRConfig();
  const { receipt, receipt_id, isLoading, error, form, nonSavedUpdates } =
    useReceiptUpdate();
  const program = useProgram();

  const { trigger, isMutating } = useSWRMutation(
    adminV2Receipts.update.path({ id: receipt_id }),
    fetcher,
  );

  if (receipt && receipt.program_id.toString() !== program?.id?.toString()) {
    return <Navigate to={`/admin/v2/programs/${program.id}/receipts`} />;
  }

  if (isLoading)
    return (
      <div className="h-screen w-full flex justify-center items-center animate animate-pulse">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="h-screen w-full flex justify-center">
        Error: {error.message}
      </div>
    );

  const images = receipt ? receipt["images"] : [];
  const processedAnalysisData = receipt ? receipt["processed_data"] : {};

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast({
        title: "Error!",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const formData = serialize(form.getValues());
    const result = await trigger({ formData, method: "PUT" });

    if (!result.success) {
      toast({
        title: "Error!",
        description: result.errors,
        variant: "destructive",
      });
    }
    if (result.success) {
      mutate(() => true, undefined, {
        revalidate: true,
      });

      toast({
        title: "Success!",
        description: "Receipt has been updated successfully.",
      });
    }
  };

  const integrityNotSelected =
    receipt?.status === "needs_human_review" && !form.watch("integrity");
  const reasonNotSelected =
    receipt?.status === "needs_human_review" &&
    form.watch("integrity") === "invalid" &&
    !form.watch("admin_invalidation_reason");

  const disabled =
    isMutating ||
    nonSavedUpdates.length > 0 ||
    integrityNotSelected ||
    reasonNotSelected;

  const disabledReason = () => {
    if (integrityNotSelected) {
      return "Please select an integrity status before saving.";
    }

    if (reasonNotSelected) {
      return "Please select a reason for rejection before saving.";
    }

    if (isMutating) {
      return "Saving...";
    }
    if (nonSavedUpdates.length > 0) {
      return "Please save changes before submitting.";
    }

    return "";
  };

  return (
    <div className="container mx-auto py-4">
      <h1 className="text-2xl font-bold mb-4">Receipt Review</h1>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="flex items-center gap-x-2">
          <Tooltip content={disabledReason()}>
            <div className="">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" disabled={disabled}>
                    <Save className="mr-2 size-4" />
                    Save Changes
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Points will be given or removed based on the changes made
                      to the receipt. The receipt status will be moved to
                      processed. Are you sure you want to proceed?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button
                      onClick={handleSubmit}
                      type="button"
                      disabled={disabled}
                    >
                      <Save className="mr-2 size-4" />
                      Save Changes
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Tooltip>
        </div>
      </div>

      <div className="grid lg:grid-cols-[500px_1fr] gap-4">
        <ReceiptAnalysis
          images={images}
          processedAnalysisData={processedAnalysisData}
        />

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="overview">Receipt Overview</TabsTrigger>
            <TabsTrigger value="items">Item Review</TabsTrigger>
            <TabsTrigger value="activity">Admin Activity Log</TabsTrigger>
            <TabsTrigger value="participant_history">
              Participant history
            </TabsTrigger>
            <TabsTrigger value="submit_issue">Submit Issue</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <ReceiptDetailsCard />
          </TabsContent>
          <TabsContent value="items">
            <ReceiptItems />
          </TabsContent>
          <TabsContent value="activity">
            <ReceiptActivity />
          </TabsContent>
          <TabsContent value="participant_history">
            <ParticipantHistory />
          </TabsContent>
          <TabsContent value="submit_issue">
            <ReceiptSubmitIssue />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ReceiptOverviewPage() {
  const { receipt_id } = useParams();

  return (
    <ReceiptUpdateProvider receiptId={receipt_id}>
      <ReceiptOverview />
    </ReceiptUpdateProvider>
  );
}

export default withPermission(ReceiptOverviewPage, "receipts", "update");
